import React, { useMemo } from 'react'

import type { UploadProps } from '@fourthwall/components'
import { InputUI, Subsection, Toasts, Upload, useMediaQueryContext } from '@fourthwall/components'
import { ExclamationMarkIcon, PhotoIcon, UploadIcon } from '@fourthwall/icons'
import { useFormikContext } from '@fourthwall/utils/dist/formik'

import { getRejectionErrors } from '../../../../../../../utils/dropzone'
import AudioPlayer from '../../../../components/AudioPlayer'
import { RemovableUpload } from '../../../../components/RemovableUpload/RemovableUpload'
import { UploadedImage } from '../../../../components/UploadedImage/UploadedImage'
import { ALLOWED_AUDIO_EXTENSIONS } from '../../../../consts'
import type { Post } from '../../../../types'
import { Overlay } from '../../components/Overlay'
import type { FormValues } from '../../types'
import { FormFields } from '../../types'
import * as S from './AudioFields.styled'

type Props = {
  isUploading?: boolean
  uploadProgress?: number
  status?: Post['statuses']['resources']
}

export const AudioFields = ({ isUploading, uploadProgress, status }: Props) => {
  const media = useMediaQueryContext()
  const { values, errors, setFieldValue } = useFormikContext<FormValues>()
  const audioThumbnailError = errors[FormFields.audioThumbnail]
  const audioError = errors[FormFields.audio]

  const handleAudioThumbnailUpload = (files?: File[]) => {
    if (files?.[0]) {
      setFieldValue(FormFields.audioThumbnail, files[0])
    }
  }

  const handleAudioUpload = (files?: File[]) => {
    if (files?.[0]) {
      setFieldValue(FormFields.audio, files[0])
    }
  }

  const renderThumbnail = () => {
    if (values.audioThumbnail) {
      return (
        <S.ThumbnailContainer>
          <RemovableUpload onRemove={() => setFieldValue(FormFields.audioThumbnail, undefined)}>
            <UploadedImage image={values.audioThumbnail} />
          </RemovableUpload>
          {isUploading && <Overlay />}
        </S.ThumbnailContainer>
      )
    }

    return (
      <S.ThumbnailContainer>
        <Upload
          accept={{
            'image/*': [],
          }}
          title=""
          icon={PhotoIcon}
          iconSize={24}
          maxSize={10485760}
          orientation="horizontal"
          invalid={!!errors[FormFields.audioThumbnail]}
          onFileDrop={handleAudioThumbnailUpload}
          onFileDropRejected={handleFileReject}
        />
      </S.ThumbnailContainer>
    )
  }

  const renderOverlay = () => {
    if (status === 'Errored') {
      return (
        <Overlay
          type="error"
          showLoader={false}
          title="An error occured during audio processing. Please re-upload your audio."
          icon={ExclamationMarkIcon}
        />
      )
    }

    if (status === 'Processing') {
      return (
        <Overlay
          title="Processing"
          subTitle="This process can take up to an hour, depending on the audio file size."
          showLoader={false}
        />
      )
    }
  }

  const renderUploadingOverlay = () => {
    const getMessage = () => {
      if (uploadProgress === 0) {
        return 'Uploading'
      }

      return `Uploading (${uploadProgress}%)`
    }

    return <Overlay title={getMessage()} />
  }

  const handleFileReject: UploadProps['onFileDropRejected'] = (rejectedFiles) => {
    getRejectionErrors(rejectedFiles).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const audioSrc = useMemo(() => {
    if (!values.audio) {
      return ''
    }

    if (typeof values.audio === 'string') {
      return values.audio
    }

    return URL.createObjectURL(values.audio)
  }, [values.audio])

  const renderAudio = () => {
    const audio = values.audio

    if (status === 'Processing' || status === 'Errored') {
      return (
        <S.PlayerContainer>
          <S.OverlayContainer>{renderOverlay()}</S.OverlayContainer>
        </S.PlayerContainer>
      )
    }

    if (values.audio) {
      return (
        <S.PlayerContainer>
          <RemovableUpload
            withBackground={false}
            onRemove={() => setFieldValue(FormFields.audio, undefined)}
          >
            <AudioPlayer audioSrc={audioSrc} size="small" hls={typeof audio === 'string'} />
          </RemovableUpload>
          {isUploading && renderUploadingOverlay()}
        </S.PlayerContainer>
      )
    }

    return (
      <S.PlayerContainer>
        <Upload
          accept={ALLOWED_AUDIO_EXTENSIONS}
          title={
            media.tablet ? (
              <span>
                Drop audio or <u>browse</u>
              </span>
            ) : (
              'Browse'
            )
          }
          maxSize={53687091200} // 50 GB
          icon={UploadIcon}
          iconSize={20}
          orientation="horizontal"
          invalid={!!errors[FormFields.audio]}
          onFileDrop={handleAudioUpload}
          onFileDropRejected={handleFileReject}
        />
      </S.PlayerContainer>
    )
  }

  return (
    <Subsection title="Audio">
      <S.Container>
        {renderThumbnail()}
        {renderAudio()}
      </S.Container>
      <div style={{ display: 'grid' }}>
        {audioThumbnailError && <InputUI.Error>{audioThumbnailError}</InputUI.Error>}
        {audioError && <InputUI.Error>{audioError}</InputUI.Error>}
      </div>
    </Subsection>
  )
}
